import { FC, Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { createCaseShare } from 'app/store/CaseShares/CaseShares.async';
// Selectors
import { selectLoading } from 'app/store/CaseShares/CaseShares.selectors';
import {
  selectDocumentsEntities, selectDocumentsAsOptions
} from 'app/store/DMSDocuments/DMSDocuments.selectors';
// Mui
import { Box, FormHelperText } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Input, Button } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
// Utility
import { isRequired, isEmailValid } from 'app/utilities/Validations';

interface IFormData {
  insuranceCaseId?: number;
  documentIds: number[];
  options?: string[];
  notificationEmail: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
}

const ShareFormDialog:FC<Props> = ({
  // Props
  open, onClose
}) => {
  const { t } = useTranslation();

  const { caseId } = useParams<{ caseId:string }>();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const documentEntities = useAppSelector(selectDocumentsEntities);
  const documentOptions = useAppSelector(selectDocumentsAsOptions);
  const loading:boolean = useAppSelector(selectLoading);

  const { control, handleSubmit, formState: { errors }, watch, setValue, clearErrors } = useForm<IFormData>({
    defaultValues: {
      documentIds: [],
      options: [],
      notificationEmail: ''
    }
  });

  const onSubmit = handleSubmit(async (data:IFormData) => {
    try {
      const { options, ...otherData } = data;
      const nextData:IFormData = {
        ...otherData,
        insuranceCaseId: Number(caseId)
      };
      if ( options && options.length ) nextData['options'] = options;
      await dispatch(createCaseShare(nextData)).unwrap();
      onClose();
    } catch(error){}
  });

  const handleSelectAllOptionsClick = () => {
    setValue('options', optionOptions.map(({ value }) => value));
  }

  const handleSelectAllDocumentsClick = () => {
    setValue('documentIds', documentOptions.map(({ value }) => value));
    if ( errors.documentIds ) clearErrors('documentIds');
  }

  const optionOptions:any[] = [
    { value: 'episode.labels', label: t('labels.episodeLabels') },
    { value: 'episode.notes', label: t('labels.episodeNotes') },
    { value: 'episodeFacts', label: t('labels.episodeFacts') },
    { value: 'page.tag', label: t('labels.pageTag') },
    { value: 'page.colors', label: t('labels.pageColors') },
    { value: 'page.highlights', label: t('labels.pageHighlights') },
    { value: 'page.inWorkspace', label: t('labels.pageInWorkspace') },
    { value: 'document.labels', label: t('labels.documentLabels') }
  ];

  const watchDocumentIds = watch('documentIds');

  const totalNumberOfPages = [...(watchDocumentIds || [])].reduce((acc:number, cur:number) => {
    const documentEntity = documentEntities[cur];
    return acc + (documentEntity.numberOfPages || 0);
  }, 0);

  const actions = (
    <Fragment>
      <Button
        name="Cancel share case dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name="Submit share case dialog"
        loading={loading}
        onClick={onSubmit}
        variant="contained"
        color="primary"
      >{t('buttons.share')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('pages.caseShares.formTitle')}
      actions={actions}

      PaperProps={{
        component: 'form',
        onSubmit,
        noValidate: true
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <Controller
          control={control} name="options"
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={optionOptions}
              TextFieldProps={{
                label: t('labels.options'),
                name: field.name
              }}
              multiple={true}
              limitTags={3}
              filterSelectedOptions
            />
          )}
        />
        <Button
          name="Select all options"
          sx={{ mt: 6.5 }}
          onClick={handleSelectAllOptionsClick}
          variant="contained"
        >{t('buttons.all')}</Button>
      </Box>

      {/* Documents */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <Controller
          control={control} name="documentIds"
          rules={{ required: isRequired }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={documentOptions || []}
              TextFieldProps={{
                label: t('labels.documents'),
                name: field.name,
                required: true,
                error: Boolean(errors.documentIds),
                helperText: (errors.documentIds as any)?.message || '',
              }}
              groupBy={(option) => option.props.collection.name}
              renderGroup={(params) => (
                <Box key={params.key}>
                  <Box sx={{
                    bgcolor: 'rgb(238,238,238)',
                    color: 'rgba(0,0,0,0.87)',
                    fontWeight: 700,
                    py: 2,
                    px: 4
                  }}>{params.group}</Box>
                  {params.children}
                </Box>
              )}
              multiple={true}
              limitTags={3}
              filterSelectedOptions
            />
          )}
        />
        <Button
          name="Select all documents"
          sx={{ mt: 6.5 }}
          onClick={handleSelectAllDocumentsClick}
          variant="contained"
        >{t('buttons.all')}</Button>
      </Box>

      <FormHelperText>{t('labels.totalPages')}: {totalNumberOfPages}</FormHelperText>

      {/* Notification e-mail */}
      <Controller
        control={control} name="notificationEmail"
        rules={{ required: isRequired, pattern: isEmailValid }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('labels.notificationEmail')} type="email"
            required
            error={Boolean(errors.notificationEmail)}
            helperText={errors.notificationEmail ? errors.notificationEmail.message : ''}
          />
        )}
      />
      <FormHelperText>{t('pages.caseShares.formHint')}</FormHelperText>
    </Dialog>
  )
}

export default ShareFormDialog;
